<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { useDialogFocus } from '@ilteducation/components';
import NextButton from '../components/NextButton.vue';
import AddVideoDialog from '../components/host/AddVideoDialog.vue';
import SaveQuizterDialog from '../components/host/SaveQuizterDialog.vue';
import VideoList from '../components/VideoList.vue';
import QuizterLayout from '../layout/QuizterLayout.vue';
import useLocale from '../../composables/use-locale';
import EditVideoQuizDialog from '../components/host/EditVideoQuizDialog.vue';
import { useVideoSelection } from '../composables/useVideoSelection';
import type { SelectedVideo } from '../composables/useQuizterLoader';

type SavePayload = {
  resourceIds: string[];
  name: string;
  description?: string;
  questions?: CloseEndedQuestion[];
};

const props = withDefaults(
  defineProps<{
    initialVideos?: SelectedVideo[];
    initialName?: string;
    initialDescription?: string;
    loading?: boolean;
    saved?: boolean;
    start: (resourceIds: string[], questions?: CloseEndedQuestion[]) => void | Promise<void>;
    save: (payload: SavePayload) => Promise<void>;
  }>(),
  {
    initialVideos: () => [],
    initialName: '',
    initialDescription: '',
    loading: false,
    saved: false,
  },
);

const emit = defineEmits<{
  (e: 'back'): void;
}>();

const { t } = useLocale();
const showErrorSnackbar = ref(false);

const showSaveDialog = ref(false);
const isStarting = ref(false);
const isSaving = ref(false);
const isSaved = ref(props.saved);
const showAnimation = ref(props.saved);

// Any change to the selection means the saved snapshot is stale again.
const markDirty = () => {
  isSaved.value = false;
  showAnimation.value = false;
};

const {
  videos,
  excludedResourceIds,
  showAddVideoDialog,
  showEditDialog,
  editVideo,
  openAddVideoDialog,
  handleAddVideo,
  handleRemoveVideo,
  handleEditVideoQuiz,
  handleEditSave,
} = useVideoSelection({ allowRemoveAll: true, onChange: markDirty });

// Seed (and re-seed) the working list from the input. In practice the wrapper resolves the
// initial videos once, before the user interacts, so this doesn't clobber in-progress edits.
watch(
  () => props.initialVideos,
  (initialVideos) => {
    videos.value = initialVideos.map((v) => ({ ...v }));
  },
  { immediate: true },
);

const { rememberFocus } = useDialogFocus([showSaveDialog]);

const hasVideos = computed(() => videos.value.length > 0);

// Flatten the working list into the payload both start and save need.
const collectSelection = () => {
  const resourceIds = videos.value.map((v) => v.resourceId);
  const flatQuestions = videos.value.flatMap((v) => v.selectedQuestions ?? []);
  const questions = flatQuestions.length > 0 ? flatQuestions : undefined;
  return { resourceIds, questions };
};

const handleStartGame = async () => {
  if (!hasVideos.value) {
    return;
  }
  const { resourceIds, questions } = collectSelection();
  isStarting.value = true;
  try {
    await props.start(resourceIds, questions);
  } finally {
    isStarting.value = false;
  }
};

const handleOpenSaveDialog = () => {
  rememberFocus();
  showSaveDialog.value = true;
};

const handleSaveQuizter = async ({ name, description }: { name: string; description?: string }) => {
  const { resourceIds, questions } = collectSelection();
  isSaving.value = true;
  try {
    await props.save({ resourceIds, name, description, questions });
    isSaved.value = true;
    showAnimation.value = true;
    showSaveDialog.value = false;
  } catch {
    showErrorSnackbar.value = true;
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <QuizterLayout :show-header="false" :show-back-button="true" back-button-pos="topLeft" @back="emit('back')">
    <div class="d-flex flex-column align-center justify-center ga-12">
      <h1 class="chalkduster-font text-center text-h1">Quizter!</h1>

      <!-- Loading state while the wrapper resolves the initial videos -->
      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate size="48"></v-progress-circular>
      </div>

      <template v-else>
        <!-- Video List and Add video button -->
        <VideoList
          :videos="videos"
          :allow-remove-all="true"
          :show-edit="true"
          @remove="handleRemoveVideo"
          @edit="handleEditVideoQuiz"
        >
          <v-btn variant="outlined" class="add-video-btn" rounded="lg" :ripple="true" @click="openAddVideoDialog">
            <div class="d-flex flex-column align-center">
              <v-icon icon="fa:fawsb fa-video" size="60"></v-icon>
              <p class="chalkboard-font text-center text-h6 ma-0">
                {{ t('quizter.add_video_clarification_home_page') }}
              </p>
            </div>
          </v-btn>
        </VideoList>

        <!-- Action Buttons -->
        <div class="button-row">
          <NextButton
            size="large"
            :class="{ 'button-animation': showAnimation }"
            :disabled="!hasVideos"
            :loading="isStarting"
            @click="handleStartGame"
            data-testid="quizter-start-button"
          >
            <template #prepend>
              <v-icon class="mr-2" icon="fa:fawsb fa-trophy"></v-icon>
            </template>
            {{ t('quizter.start_game') }}
          </NextButton>

          <v-btn
            size="large"
            variant="outlined"
            :disabled="!hasVideos || isSaved"
            @click="handleOpenSaveDialog"
            data-testid="quizter-save-button"
            class="chalkboard-font save-button"
          >
            <template #prepend>
              <v-icon class="mr-2" icon="fa:fawsb fa-floppy-disk"></v-icon>
            </template>
            {{ t('save_quizter') }}
          </v-btn>
        </div>
      </template>

      <!-- Add Video Dialog -->
      <AddVideoDialog
        v-model="showAddVideoDialog"
        :exclude-resource-ids="excludedResourceIds"
        @select="handleAddVideo"
      />

      <!-- Save Quizter Dialog -->
      <SaveQuizterDialog
        v-model="showSaveDialog"
        :loading="isSaving"
        :initial-name="initialName"
        :initial-description="initialDescription"
        @save="handleSaveQuizter"
      />

      <!-- Edit Video quiz Dialog-->
      <EditVideoQuizDialog v-if="!!editVideo" v-model="showEditDialog" :video="editVideo" @save="handleEditSave" />

      <v-snackbar v-model="showErrorSnackbar" color="error" location="bottom">
        {{ t('save_dialog.save_error') }}
      </v-snackbar>
    </div>
  </QuizterLayout>
</template>

<style scoped lang="scss">
.button-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  width: 100%;
  max-width: 380px;
}

.empty-state {
  min-height: 200px;
  max-width: 500px;
}

.add-video-btn {
  height: 191px;
  width: 340px;
}

.add-video-btn,
.save-button {
  border: 3px solid rgb(var(--v-theme-outline));
}

.button-animation {
  animation: glow 5s ease-in-out forwards;
}

@keyframes glow {
  0% {
    box-shadow: 0 0 0 0 transparent;
    transform: scale(1);
  }
  25% {
    box-shadow: 0 0 14px 3px rgba(#f2f2f3, 0.5);
    transform: scale(1.05);
  }
  50% {
    box-shadow: 0 0 0 0 transparent;
    transform: scale(1);
  }
  75% {
    box-shadow: 0 0 14px 3px rgba(#f2f2f3, 0.5);
    transform: scale(1.05);
  }
  100% {
    box-shadow: 0 0 0 0 transparent;
    transform: scale(1);
  }
}
</style>
